import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import onboardingService from '../services/onboardingService'
import MainLayout from '../components/layouts/MainLayout.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    name: 'Auth',
    component: () => import('../views/AuthPage.vue'),
    meta: { title: 'Authentication', requiresAuth: false, hideForAuth: true }
  },
  {
    path: '/auth-test',
    name: 'AuthTest',
    component: () => import('../views/AuthPageTest.vue'),
    meta: { title: 'Auth Test', requiresAuth: false }
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('../views/OnboardingView.vue'),
    meta: { title: 'Get Started', requiresAuth: true }
  },
  {
    path: '/auth/linkedin/callback',
    name: 'LinkedInCallback',
    component: () => import('../views/LinkedInCallbackView.vue'),
    meta: { title: 'LinkedIn Connection', requiresAuth: false }
  },
  // Additional LinkedIn callback routes to match B2C patterns
  {
    path: '/auth/linkedin-callback',
    name: 'LinkedInCallbackAlt',
    component: () => import('../views/LinkedInCallbackView.vue'),
    meta: { title: 'LinkedIn Connection', requiresAuth: false }
  },
  {
    path: '/auth/linkedin-settings-callback',
    name: 'LinkedInSettingsCallback',
    component: () => import('../views/LinkedInCallbackView.vue'),
    meta: { title: 'LinkedIn Connection', requiresAuth: false }
  },
  {
    path: '/auth/linkedin-publish-callback',
    name: 'LinkedInPublishCallback',
    component: () => import('../views/LinkedInCallbackView.vue'),
    meta: { title: 'LinkedIn Connection', requiresAuth: false }
  },
  {
    path: '/auth/callback',
    name: 'GoogleCallback',
    component: () => import('../views/GoogleCallbackView.vue'),
    meta: { title: 'Google Authentication', requiresAuth: false }
  },
  {
    path: '/auth/reset-password',
    name: 'PasswordReset',
    component: () => import('../views/PasswordResetView.vue'),
    meta: { 
      title: 'Reset Password', 
      requiresAuth: false,
      hideForAuth: true 
    }
  },
  {
    path: '/',
    redirect: '/admin/dashboard'
  },
  // Admin routes
  {
    path: '/admin',
    component: MainLayout,
    meta: { requiresAuth: true, requiredRole: 'admin' },
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('../views/TeamView.vue'),
        meta: { title: 'Business Control Panel', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'team/invite',
        name: 'InviteUser',
        component: () => import('../views/InviteUserView.vue'),
        meta: { title: 'Invite New User', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'analytics',
        name: 'AdminAnalytics',
        component: () => import('../views/AnalyticsView.vue'),
        meta: { title: 'Company Analytics', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'scheduler',
        name: 'Scheduler',
        component: () => import('../views/SchedulerView.vue'),
        meta: { title: 'Scheduler', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'team-content',
        name: 'TeamContent',
        component: () => import('../views/ContentView.vue'),
        meta: { title: 'Team Content', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'content',
        name: 'AdminContent',
        component: () => import('../views/ContentView.vue'),
        meta: { title: 'Team Content', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'content/create',
        name: 'AdminCreatePost',
        component: () => import('../views/editor/AIPostCreation/AIPostCreationView.vue'),
        meta: { title: 'Create Post with AI', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'content/new-promotion',
        name: 'NewPromotion',
        component: () => import('../views/NewPromotionView.vue'),
        meta: { title: 'New Company Promotion', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('../views/SettingsView.vue'),
        meta: { title: 'Settings', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'settings/org',
        name: 'OrgSettings',
        component: () => import('../views/OrgSettingsView.vue'),
        meta: { title: 'Admin Settings', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'settings/team',
        name: 'TeamSettings',
        component: () => import('../views/TeamSettingsView.vue'),
        meta: { title: 'Business Settings', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'settings/user',
        name: 'AdminUserSettings',
        component: () => import('../views/UserSettingsView.vue'),
        meta: { title: 'User Settings', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'settings/user/:id',
        name: 'IndividualUserSettings',
        component: () => import('../views/IndividualUserSettingsView.vue'),
        meta: { title: 'Individual User Settings', requiresAuth: true, requiredRole: 'admin' }
      },
      {
        path: 'activity-log',
        name: 'ActivityLog',
        component: () => import('../views/ActivityLogView.vue'),
        meta: { title: 'Activity Log', requiresAuth: true, requiredRole: 'admin' }
      }
    ]
  },
  // Editor routes - Same as admin but without Settings and Activity Log
  {
    path: '/editor',
    component: MainLayout,
    meta: { requiresAuth: true, requiredRole: 'editor' },
    children: [
      {
        path: 'dashboard',
        name: 'EditorDashboard',
        component: () => import('../views/TeamView.vue'),
        meta: { title: 'Dashboard', requiresAuth: true, requiredRole: 'editor' }
      },
      {
        path: 'analytics',
        name: 'EditorAnalytics',
        component: () => import('../views/AnalyticsView.vue'),
        meta: { title: 'Analytics', requiresAuth: true, requiredRole: 'editor' }
      },
      {
        path: 'scheduler',
        name: 'EditorScheduler',
        component: () => import('../views/SchedulerView.vue'),
        meta: { title: 'Scheduler', requiresAuth: true, requiredRole: 'editor' }
      },
      {
        path: 'content',
        name: 'EditorContent',
        component: () => import('../views/ContentView.vue'),
        meta: { title: 'Team Content', requiresAuth: true, requiredRole: 'editor' }
      },
      {
        path: 'content/create',
        name: 'EditorCreatePost',
        component: () => import('../views/editor/AIPostCreation/AIPostCreationView.vue'),
        meta: { title: 'Create Post with AI', requiresAuth: true, requiredRole: 'editor' }
      }
    ]
  },
  // Profile routes - New profile dashboard design
  {
    path: '/profile',
    component: MainLayout,
    meta: { requiresAuth: true, requiredRole: 'profile' },
    children: [
      {
        path: '',
        redirect: '/profile/dashboard'
      },
      {
        path: 'dashboard',
        name: 'ProfileDashboard',
        component: () => import('../views/profile/ProfileDashboardView.vue'),
        meta: { title: 'Dashboard', requiresAuth: true, requiredRole: 'profile' }
      },
      {
        path: 'content',
        name: 'ProfileContent',
        component: () => import('../views/editor/MyContentView.vue'),
        meta: { title: 'My Content', requiresAuth: true, requiredRole: 'profile' }
      },
      {
        path: 'content/create',
        name: 'ProfileCreatePost',
        component: () => import('../views/editor/AIPostCreation/AIPostCreationView.vue'),
        meta: { title: 'Create Post with AI', requiresAuth: true, requiredRole: 'profile' }
      },
      {
        path: 'analytics',
        name: 'ProfileAnalytics',
        component: () => import('../views/profile/ProfileAnalyticsView.vue'),
        meta: { title: 'My Analytics', requiresAuth: true, requiredRole: 'profile' }
      },
      {
        path: 'scheduler',
        name: 'ProfileScheduler',
        component: () => import('../views/SchedulerView.vue'),
        meta: { title: 'Scheduler', requiresAuth: true, requiredRole: 'profile' }
      },
      {
        path: 'commenter',
        name: 'ProfileCommenter',
        component: () => import('../views/editor/CommenterView.vue'),
        meta: { title: 'Smart Commenter', requiresAuth: true, requiredRole: 'profile' }
      },
      {
        path: 'settings',
        name: 'ProfileSettings',
        component: () => import('../views/profile/ProfileSettingsView.vue'),
        meta: { title: 'Settings', requiresAuth: true, requiredRole: 'profile', selfServeOnly: true }
      },
      {
        path: 'pricing',
        name: 'ProfilePricing',
        component: () => import('../views/profile/ProfilePricingView.vue'),
        meta: { title: 'Pricing', requiresAuth: true, requiredRole: 'profile', selfServeOnly: true }
      }
    ]
  },
  // Legacy redirects for backwards compatibility
  {
    path: '/team',
    redirect: '/admin/dashboard'
  },
  {
    path: '/analytics',
    redirect: '/admin/analytics'
  },
  {
    path: '/content',
    redirect: '/admin/team-content'
  },
  {
    path: '/settings',
    redirect: '/admin/settings'
  },
  {
    path: '/scheduler',
    redirect: '/admin/scheduler'
  },
  {
    path: '/activity-log',
    redirect: '/admin/activity-log'
  }
]

export default routes

// Navigation guard for authentication and role-based access
export function setupRouterGuards(router: ReturnType<typeof import('vue-router').createRouter>) {
  // Track if we've checked onboarding status this session
  let onboardingStatusChecked = false

  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()
    const requiresAuth = to.meta?.requiresAuth as boolean | undefined
    const hideForAuth = to.meta?.hideForAuth as boolean | undefined
    const requiredRole = to.meta?.requiredRole as 'admin' | 'editor' | 'profile' | undefined

    // Check for password reset token in URL hash (Supabase redirects to root with hash)
    // This handles cases where Supabase redirects to https://b2b.noovox.com#access_token=...&type=recovery
    if (to.path === '/' || to.path === '') {
      const hash = window.location.hash
      if (hash) {
        const params = new URLSearchParams(hash.substring(1))
        const accessToken = params.get('access_token')
        const type = params.get('type')
        
        if (accessToken && type === 'recovery') {
          console.log('[Router Guard] Detected password reset token in hash, redirecting to /auth/reset-password')
          // Redirect to password reset page and preserve the hash
          // The hash will be available in window.location.hash on the target page
          next({ path: '/auth/reset-password', replace: true })
          return
        }
      }
    }

    // Check for password reset token in redirect query parameter
    // This handles cases where Supabase redirects to /auth?redirect=/admin/dashboard%23access_token=...&type=recovery
    if (to.path === '/auth') {
      const redirectParam = to.query.redirect as string
      if (redirectParam) {
        // Decode the redirect parameter (it may contain %23 for #)
        const decodedRedirect = decodeURIComponent(redirectParam)
        
        // Check if it contains a hash fragment with access_token
        const hashIndex = decodedRedirect.indexOf('#')
        if (hashIndex !== -1) {
          const hashFragment = decodedRedirect.substring(hashIndex + 1)
          const params = new URLSearchParams(hashFragment)
          const accessToken = params.get('access_token')
          const type = params.get('type')
          
          if (accessToken && type === 'recovery') {
            console.log('[Router Guard] Detected password reset token in redirect query param, redirecting to /auth/reset-password')
            console.log('[Router Guard] Hash fragment:', hashFragment)
            // Use window.location to properly set the hash
            window.location.replace(`/auth/reset-password#${hashFragment}`)
            return
          }
        }
      }
    }

    console.log('[Router Guard] ===== Navigation Start =====')
    console.log('[Router Guard] From:', from.path, '-> To:', to.path)
    console.log('[Router Guard] isLoggedIn:', authStore.isLoggedIn, 'isAuthenticated:', authStore.isAuthenticated, 'hasToken:', !!authStore.accessToken)
    console.log('[Router Guard] actualUserRole:', authStore.actualUserRole, 'userRole:', authStore.userRole)
    console.log('[Router Guard] requiresAuth:', requiresAuth, 'requiredRole:', requiredRole)
    console.log('[Router Guard] hasCompletedOnboarding:', authStore.hasCompletedOnboarding)

    // Reset post creation store when navigating to create new post (not editing)
    const isPostCreationRoute = to.name === 'AdminCreatePost' || to.name === 'EditorCreatePost' || to.name === 'ProfileCreatePost'
    const isEditing = to.query.edit !== undefined
    if (isPostCreationRoute && !isEditing) {
      // Dynamically import to avoid circular dependencies
      const { usePostCreationStore } = await import('../stores/postCreation')
      const postCreationStore = usePostCreationStore()
      console.log('[Router Guard] Resetting post creation store for new post')
      postCreationStore.reset()
    }

    // Reset onboarding check flag if user is not logged in (handles logout case)
    if (!authStore.isLoggedIn) {
      onboardingStatusChecked = false
    }

    // If user is authenticated but we haven't checked onboarding status from API yet, check it now
    if (authStore.isLoggedIn && !onboardingStatusChecked) {
      try {
        console.log('[Router Guard] Checking onboarding status from API...')
        const status = await onboardingService.getOnboardingStatus()
        console.log('[Router Guard] API returned onboarding status:', status)
        console.log('[Router Guard] status.completed:', status.completed, 'type:', typeof status.completed)
        // Ensure we convert to boolean explicitly
        const completed = Boolean(status.completed)
        
        // Only update onboarding status if it's true (completed)
        // If it's false, preserve existing status to avoid redirecting to onboarding
        // when LinkedIn is disconnected but onboarding was already completed
        if (completed) {
          authStore.setOnboardingCompleted(true)
        } else {
          // If API says not completed, check if we have a stored completed status
          // This handles cases where LinkedIn is disconnected but onboarding was completed
          const storedCompleted = authStore.hasCompletedOnboarding
          if (storedCompleted) {
            console.log('[Router Guard] API says not completed, but user has completed onboarding before. Preserving completed status.')
            // Don't update - keep the existing completed status
          } else {
            // User hasn't completed onboarding, update status
            authStore.setOnboardingCompleted(false)
          }
        }
        
        onboardingStatusChecked = true
        // Re-read the value to verify it was set correctly
        const updatedValue = authStore.hasCompletedOnboarding
        console.log('[Router Guard] Updated hasCompletedOnboarding to:', updatedValue, 'type:', typeof updatedValue)
      } catch (error) {
        console.warn('[Router Guard] Failed to check onboarding status:', error)
        // If check fails (e.g., LinkedIn disconnected causing API error),
        // preserve existing onboarding status to avoid redirecting to onboarding
        // The http interceptor will handle token refresh automatically
        const existingStatus = authStore.hasCompletedOnboarding
        if (existingStatus) {
          console.log('[Router Guard] Onboarding status check failed, but user has completed onboarding. Preserving status to avoid redirect.')
          // Keep the existing completed status
        }
        onboardingStatusChecked = true
      }
    }

    // Get the current onboarding status after potential update
    const hasCompletedOnboarding = Boolean(authStore.hasCompletedOnboarding)

    // If route requires auth and user is not authenticated
    if (requiresAuth && !authStore.isLoggedIn) {
      console.log('[Router Guard] REDIRECT TO AUTH - requiresAuth:', requiresAuth, 'isLoggedIn:', authStore.isLoggedIn)
      next({ name: 'Auth', query: { redirect: to.fullPath } })
      return
    }

    // If route should be hidden when authenticated (like login page)
    if (hideForAuth && authStore.isLoggedIn) {
      // Check if user needs to complete onboarding first
      if (!hasCompletedOnboarding) {
        console.log('[Router Guard] User needs onboarding, redirecting to /onboarding')
        next('/onboarding')
        return
      }
      // Redirect based on current viewing role
      const redirectPath = getDashboardPath(authStore.userRole)
      next(redirectPath)
      return
    }

    // Check if user needs to complete onboarding (except for onboarding-related routes)
    const onboardingExemptRoutes = ['Onboarding', 'LinkedInCallback', 'LinkedInCallbackAlt', 'LinkedInSettingsCallback', 'LinkedInPublishCallback']
    if (
      requiresAuth &&
      authStore.isLoggedIn &&
      !hasCompletedOnboarding &&
      !onboardingExemptRoutes.includes(to.name as string)
    ) {
      console.log('[Router Guard] User needs onboarding, redirecting to /onboarding')
      next('/onboarding')
      return
    }

    // Check role-based access
    if (requiredRole && authStore.isLoggedIn) {
      // Use actualUserRole to determine access permissions
      // Admin users can access ALL routes (admin, editor, and profile routes)
      // This allows admins to switch to "editor view" and still access editor routes
      // Profile users can ONLY access profile routes
      // Fallback to userRole if actualUserRole is not set (for existing sessions)
      const actualRole = authStore.actualUserRole || authStore.userRole

      // Debug logging (remove in production)
      console.log('[Router Guard] Route:', to.path, 'Required role:', requiredRole, 'Actual role:', actualRole, 'Current role:', authStore.userRole)

      // Fallback: if no role is set but user is logged in, allow access
      // This handles edge cases during role switching
      if (!actualRole) {
        console.log('[Router Guard] No role set, allowing access for logged-in user')
        next()
        return
      }

      // Actual admins can access everything (admin, editor, and profile routes)
      if (actualRole === 'admin') {
        next()
        return
      }

      // Editors can access both editor and profile routes (when they switch roles)
      if (actualRole === 'editor') {
        // Editor trying to access admin route - not allowed, redirect to editor dashboard
        if (requiredRole === 'admin') {
          next('/editor/dashboard')
          return
        }
        // Editor accessing editor routes OR profile routes (when switched) - allow
        if (requiredRole === 'editor' || requiredRole === 'profile') {
          next()
          return
        }
      }

      // Profile users can only access profile routes
      if (actualRole === 'profile') {
        // Profile trying to access admin or editor routes - redirect to profile dashboard
        if (requiredRole === 'admin' || requiredRole === 'editor') {
          next('/profile/dashboard')
          return
        }
        // Profile accessing profile routes - allow
        if (requiredRole === 'profile') {
          // Check if route is self-serve only (like profile settings)
          const selfServeOnly = to.meta?.selfServeOnly as boolean | undefined
          if (selfServeOnly) {
            // Only allow access if user is a self-serve profile (no organization)
            if (authStore.organizationId) {
              console.log('[Router Guard] Business profile trying to access self-serve only route, redirecting')
              next('/profile/dashboard')
              return
            }
          }
          next()
          return
        }
      }

      // Other role checks pass through
      next()
      return
    }

    next()
  })
}

// Helper function to get dashboard path based on role
export function getDashboardPath(role: 'admin' | 'editor' | 'profile' | null): string {
  if (role === 'admin') return '/admin/dashboard'
  if (role === 'profile') return '/profile/dashboard'
  return '/editor/dashboard'
}
